import classNames from "classnames";
import React, { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useAcceptPortalInviteMutation,
  useGetPortalInviteQuery,
} from "../../../slices/PortalSlice";
import {
  PortalAlert,
  PortalAuthFrame,
  portalInput,
  portalLabel,
  portalPrimary,
} from "../components/PortalUi";
import { errorMessage, passwordProblem } from "../portal.utils";
import {
  rememberEmail,
  rememberFirm,
  setPortalSession,
} from "../portalSession";

type Choice = "password" | "code";

const option = (on: boolean) =>
  classNames(
    "flex w-full items-start gap-3 rounded-[10px] border-[1.5px] p-3.5 text-left",
    on
      ? "border-[#6B4EFF] bg-[#FAF9FF]"
      : "border-[#DCE2EA] bg-white hover:border-[#C9BFFB]",
  );

const Radio: RadioProps = ({ on }) => (
  <span
    className={classNames(
      "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2",
      on ? "border-[#6B4EFF]" : "border-[#C3C8D4]",
    )}>
    {on && <span className={"h-2 w-2 rounded-full bg-[#6B4EFF]"} />}
  </span>
);
type RadioProps = (props: { on: boolean }) => JSX.Element;

/** The invitation link: pick how to sign in, then straight into the portal. */
export const PortalInvite = () => {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const invite = useGetPortalInviteQuery(token);
  const [accept, acceptState] = useAcceptPortalInviteMutation();
  const [choice, setChoice] = useState<Choice>("password");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const info = invite.data;

  useEffect(() => {
    if (info) {
      rememberFirm(info.firmName);
      rememberEmail(info.email);
    }
  }, [info]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (choice === "password" && !info?.hasPassword) {
      const problem = passwordProblem(password);
      if (problem) return setError(problem);
      if (password !== confirm)
        return setError("The two passwords don't match.");
    }
    const result = await accept({
      token,
      password:
        choice === "password" && !info?.hasPassword ? password : undefined,
    });
    if ("error" in result) return setError(errorMessage(result.error));
    setPortalSession(result.data);
    navigate("/portal/home", { replace: true });
  };

  if (invite.isLoading) {
    return (
      <PortalAuthFrame>
        <span className={"text-sm font-semibold text-[#6B7185]"}>
          Opening your invitation…
        </span>
      </PortalAuthFrame>
    );
  }

  if (!info || info.expired || info.alreadyAccepted) {
    const message = info?.alreadyAccepted
      ? "You've already set up your access. Sign in with your password or an emailed code."
      : info?.expired
      ? "This invitation has expired. Ask your accountant to send you a new one."
      : errorMessage(
          invite.error,
          "This invitation link isn't valid anymore. Ask your accountant to send a new one.",
        );
    return (
      <PortalAuthFrame firmName={info?.firmName}>
        <h1 className={"m-0 text-2xl font-extrabold tracking-[-0.4px]"}>
          {info?.alreadyAccepted ? "You're all set" : "This link can't be used"}
        </h1>
        <PortalAlert tone={info?.alreadyAccepted ? "info" : "error"}>
          {message}
        </PortalAlert>
        <Link to={"/portal"} className={portalPrimary}>
          Go to sign in
        </Link>
      </PortalAuthFrame>
    );
  }

  return (
    <PortalAuthFrame firmName={info.firmName}>
      <div className={"flex flex-col gap-1.5"}>
        <h1 className={"m-0 text-2xl font-extrabold tracking-[-0.4px]"}>
          {info.firstName ? `Welcome, ${info.firstName}` : "Welcome"}
        </h1>
        <span className={"text-sm font-medium leading-relaxed text-[#6B7185]"}>
          {info.firmName} invited you to the secure portal for{" "}
          <strong className={"text-[#1F2433]"}>{info.clientName}</strong>.
          You’ll sign in as{" "}
          <strong className={"text-[#1F2433]"}>{info.email}</strong>.
        </span>
      </div>

      <form
        className={"flex flex-col gap-4"}
        onSubmit={submit}
        noValidate={true}>
        <span className={portalLabel}>How do you want to sign in?</span>
        <button
          type={"button"}
          className={option(choice === "password")}
          onClick={() => setChoice("password")}
          aria-pressed={choice === "password"}>
          <Radio on={choice === "password"} />
          <span className={"flex flex-col gap-0.5"}>
            <span className={"text-sm font-extrabold"}>
              {info.hasPassword ? "Keep my password" : "Create a password"}
            </span>
            <span className={"text-[12.5px] font-medium text-[#6B7185]"}>
              {info.hasPassword
                ? "Sign in with the portal password you already have."
                : "Sign in with your email and a password."}
            </span>
          </span>
        </button>
        <button
          type={"button"}
          className={option(choice === "code")}
          onClick={() => setChoice("code")}
          aria-pressed={choice === "code"}>
          <Radio on={choice === "code"} />
          <span className={"flex flex-col gap-0.5"}>
            <span className={"text-sm font-extrabold"}>
              Email me a code each time
            </span>
            <span className={"text-[12.5px] font-medium text-[#6B7185]"}>
              No password to remember: we send a 6-digit code when you sign in.
            </span>
          </span>
        </button>

        {choice === "password" && !info.hasPassword && (
          <>
            <div className={"flex flex-col gap-1.5"}>
              <label htmlFor={"portal-new-password"} className={portalLabel}>
                Password
              </label>
              <input
                id={"portal-new-password"}
                type={"password"}
                autoComplete={"new-password"}
                className={classNames(portalInput, "font-medium")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className={"text-xs font-medium text-[#6B7185]"}>
                At least 8 characters, with letters and a number or symbol.
              </span>
            </div>
            <div className={"flex flex-col gap-1.5"}>
              <label
                htmlFor={"portal-confirm-password"}
                className={portalLabel}>
                Type it again
              </label>
              <input
                id={"portal-confirm-password"}
                type={"password"}
                autoComplete={"new-password"}
                className={classNames(portalInput, "font-medium")}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          </>
        )}

        {error && <PortalAlert>{error}</PortalAlert>}
        <button
          type={"submit"}
          className={portalPrimary}
          disabled={acceptState.isLoading}>
          {acceptState.isLoading ? "Setting up…" : "Continue to the portal"}
        </button>
      </form>
    </PortalAuthFrame>
  );
};
